import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import Icon from "@mdi/react";
import {
    mdiBankTransfer,
    mdiMagnify,
    mdiClose,
    mdiChevronDown,
    mdiCreditCardCheckOutline,
    mdiCreditCardSyncOutline,
    mdiCreditCardRefund,
    mdiCheck,
    mdiHelpCircle,
    mdiHelpCircleOutline,
} from "@mdi/js";
import AppHeader from "../components/AppHeader";
import ReconcileHelpModal from "../components/ReconcileHelpModal";
import DateInputWithPicker from "../components/DateInputWithPicker";
import { formatCurrencyFromMinor } from "../utils/currency";
import { useAuth } from "../contexts/AuthContext";
import { billingApi } from "../api/billing";
import "./Common.css";
import "./ReconcilePayments.css";

/**
 * ReconcilePayments (UI label "Reconcile Payments") — the accounting reconcile
 * ledger for external payments (cash, EFTPOS, bank transfer, other).
 *
 * Two-axis model (external-payment-assert-reconcile-lifecycle PRP):
 *   - paid       = the school got their money — already TRUE for every row here (all green)
 *   - reconciled = matched against the external source of truth (bank statement / till)
 *
 * This page lists ALL external payments — reconciled AND unreconciled — by querying the
 * `transactions` ledger (paidOutsidePlatform: true). Reconcile is an OPTIONAL, admin-paced
 * accounting tick; it is NOT "mark paid" (marking paid now happens in the AdminPaymentModal /
 * booking admin, not here). Reconciled rows stay visible. Card (Stripe) payments are
 * reconciled-by-definition and never appear here.
 *
 * Route: /reconcile (renamed from the legacy /manage-bank-transfers; this page reconciles
 * ALL external payment rails, not just bank transfers).
 * Portal type: Sub-Portal Feature (.sub-portal-header-card).
 * Auth: requires viewFinancialReports capability — gated by ProtectedRoute in App.js,
 * plus a defense-in-depth re-check inside the component.
 */
const RAIL_OPTIONS = [
    { value: "bank_transfer", label: "Bank transfer" },
    { value: "eftpos", label: "EFTPOS" },
    { value: "cash", label: "Cash" },
    { value: "other", label: "Other" },
];
const railLabel = rail => RAIL_OPTIONS.find(r => r.value === rail)?.label || "Other";

// Money-direction filter: 'in' = payments received, 'out' = refunds (money returned).
const DIRECTION_OPTIONS = [
    { value: "all", label: "All money" },
    { value: "in", label: "Payments" },
    { value: "out", label: "Refunds" },
];

const formatAmount = (amount, currency = "AUD") => formatCurrencyFromMinor(amount, currency);

// "Friday, 15 May 2026" — readable, unambiguous, locale-independent
const formatDate = iso => {
    if (!iso) return "";
    const dt = DateTime.fromISO(iso);
    return dt.isValid ? dt.toFormat("EEEE, d LLLL yyyy") : "";
};

/**
 * One reconcile row. Reconciled rows show a green check + the match note. Unreconciled rows
 * show a green sync icon and a "Reconcile" affordance that expands an inline form (editable
 * rail correction + optional match note). Reconciling updates the row in place — it stays visible.
 */
const ReconcileRow = ({ row, onReconciled }) => {
    const [expanded, setExpanded] = useState(false);
    const [rail, setRail] = useState(row.rail || "other");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const reconciled = !!row.reconciledAt;
    // Money-out (external refund) rows render with the refund icon, a leading minus, and the
    // danger colour. `direction` is the authoritative signal — never derived from the amount sign
    // (the API already returns `amount` as a positive magnitude).
    const isRefund = row.direction === "out";

    const handleReconcile = async () => {
        setSubmitting(true);
        try {
            const res = await billingApi.reconcileTransaction(row.id, {
                rail,
                reconciledAgainst: note.trim() || null,
            });
            toast.success("Payment reconciled.");
            onReconciled(res.transaction);
            setExpanded(false);
        } catch (err) {
            toast.error(err.message || "Failed to reconcile payment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mbt-row">
            <div className="mbt-row-main">
                <div className="mbt-row-top">
                    <Icon
                        path={isRefund ? mdiCreditCardRefund : reconciled ? mdiCreditCardCheckOutline : mdiCreditCardSyncOutline}
                        size={0.95}
                        color={
                            isRefund
                                ? "var(--dl-color-turquoise-default)"
                                : "var(--dl-color-greentick)"
                        }
                        className="mbt-row-icon"
                    />
                    <span className="mbt-student">{row.studentName}</span>
                    <span className={`mbt-amount${isRefund ? " mbt-amount-out" : ""}`}>
                        {isRefund ? "-" : ""}
                        {formatAmount(row.amount, row.currency)}
                    </span>
                    {isRefund && <span className="mbt-refund-tag">Refund</span>}
                    <span className="mbt-rail-tag">{railLabel(row.rail)}</span>
                    <span className="mbt-ref">{row.referenceCode || "no code"}</span>
                </div>
                {row.studentEmail && <div className="mbt-row-email">{row.studentEmail}</div>}
                <div className="mbt-row-meta">{row.context}</div>
                {reconciled && (
                    <div className="mbt-row-reconciled">
                        <Icon path={mdiCheck} size={0.7} color="var(--dl-color-blue-default)" />
                        Reconciled {formatDate(row.reconciledAt)}
                        {row.reconciledAgainst ? ` · ${row.reconciledAgainst}` : ""}
                    </div>
                )}
            </div>
            <div className="mbt-row-actions">
                {reconciled ? (
                    <span className="mbt-reconciled-pill">Reconciled</span>
                ) : !expanded ? (
                    <button className="mbt-reconcile-btn" onClick={() => setExpanded(true)}>
                        <span className="mbt-btn-inner">
                            <Icon path={mdiCheck} size={0.75} />
                            Reconcile
                        </span>
                    </button>
                ) : (
                    <div className="mbt-reconcile-form">
                        <div className="mbt-select-wrapper">
                            <select
                                className="mbt-select"
                                value={rail}
                                onChange={e => setRail(e.target.value)}
                                aria-label="Payment rail">
                                {RAIL_OPTIONS.map(r => (
                                    <option key={r.value} value={r.value}>
                                        {r.label}
                                    </option>
                                ))}
                            </select>
                            <Icon path={mdiChevronDown} size={0.7} className="mbt-select-icon" />
                        </div>
                        <input
                            type="text"
                            className="mbt-note-input"
                            placeholder="Optional match note (bank ref, till, etc.)"
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            maxLength={500}
                        />
                        <button className="mbt-reconcile-confirm" onClick={handleReconcile} disabled={submitting}>
                            {submitting ? "Saving..." : "Mark reconciled"}
                        </button>
                        <button
                            className="mbt-reconcile-cancel"
                            onClick={() => setExpanded(false)}
                            disabled={submitting}>
                            Cancel
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const ReconcilePayments = () => {
    const navigate = useNavigate();
    const { checkPermission } = useAuth();

    const [query, setQuery] = useState("");
    const [rail, setRail] = useState("all");
    const [reconciled, setReconciled] = useState("all");
    const [direction, setDirection] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [results, setResults] = useState([]);
    const [summary, setSummary] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [helpIconHovered, setHelpIconHovered] = useState(false);

    const debounceRef = useRef(null);

    // Defense-in-depth: ProtectedRoute is the primary gate, but re-check here
    // so a JWT that loses the capability mid-session redirects rather than 500s.
    useEffect(() => {
        if (!checkPermission("viewFinancialReports")) {
            toast.error("You do not have permission to view this page.");
            navigate("/dashboard");
        }
    }, [checkPermission, navigate]);

    const runSearch = async filters => {
        setIsSearching(true);
        try {
            const data = await billingApi.searchReconciliation(filters);
            setResults(data.results || []);
            setSummary(data.summary || []);
            setHasSearched(true);
        } catch (err) {
            toast.error(err.message || "Reconciliation search failed.");
            setResults([]);
            setSummary([]);
        } finally {
            setIsSearching(false);
        }
    };

    // Debounced search on any filter change (runs on mount → lists all external payments)
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            runSearch({ q: query.trim(), rail, reconciled, direction, dateFrom, dateTo });
        }, 250);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query, rail, reconciled, direction, dateFrom, dateTo]);

    const handleClearFilters = () => {
        setQuery("");
        setRail("all");
        setReconciled("all");
        setDirection("all");
        setDateFrom("");
        setDateTo("");
    };

    // Replace the reconciled row in place — it stays visible (reconciled rows are not removed).
    const handleReconciled = updated => {
        setResults(prev =>
            prev.map(r =>
                r.id === updated.id
                    ? {
                          ...r,
                          reconciledAt: updated.reconciledAt,
                          reconciledAgainst: updated.reconciledAgainst,
                          rail: updated.processor?.type || r.rail,
                      }
                    : r
            )
        );
    };

    const emptyMessage = useMemo(() => {
        if (!isSearching && hasSearched && results.length === 0) {
            return "No external payments match these filters. External payments (cash, EFTPOS, bank transfer) appear here once recorded. Card payments reconcile automatically via Stripe and never appear here.";
        }
        return null;
    }, [isSearching, hasSearched, results.length]);

    const hasActiveFilters =
        query || rail !== "all" || reconciled !== "all" || direction !== "all" || dateFrom || dateTo;

    return (
        <div className="dashboard-container org-console reconcile-page">
            <ReconcileHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper" style={{ maxWidth: "900px" }}>
                    {/* Sub-portal header (preserved verbatim) */}
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button onClick={() => navigate("/manage-finances")} className="back-nav-btn">
                                ← Manage Finances
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon
                                    path={mdiBankTransfer}
                                    size={1.2}
                                    color="var(--dl-color-purple-default)"
                                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                                />
                                Reconcile Payments
                            </h1>
                            <div style={{ width: "100px" }} />
                        </div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
                            <p className="sub-portal-header-subtitle" style={{ margin: 0 }}>
                                Match external payments (cash, EFTPOS, bank transfer) against your bank statement at your
                                own pace. <br></br>Every payment here is already counted as paid. Stripe card payments
                                reconcile automatically.
                            </p>
                            <button
                                className="classes-help-trigger"
                                onClick={() => setHelpOpen(true)}
                                onMouseEnter={() => setHelpIconHovered(true)}
                                onMouseLeave={() => setHelpIconHovered(false)}
                                aria-label="Reconcile Payments Help"
                                title="How It Works">
                                <Icon
                                    path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                                    size="1.5rem"
                                    color="var(--dl-color-purple-default)"
                                />
                            </button>
                        </div>
                    </div>

                    {/* Filters + reconcile ledger content card */}
                    <div className="content-card">
                        <div className="mbt-search-row">
                            <div className="mbt-input-wrapper">
                                <Icon
                                    path={mdiMagnify}
                                    size={0.9}
                                    color="var(--dl-color-purple-default)"
                                    className="mbt-search-icon"
                                />
                                <input
                                    type="text"
                                    className="mbt-search-input"
                                    placeholder="Student name, reference code, or amount"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    autoFocus
                                />
                                {query && (
                                    <button
                                        type="button"
                                        className="mbt-clear-btn"
                                        onClick={() => setQuery("")}
                                        aria-label="Clear search">
                                        <Icon path={mdiClose} size={0.8} color="var(--dl-color-gray-700)" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="mbt-filters">
                            <div className="mbt-date-filters">
                                <DateInputWithPicker
                                    value={dateFrom}
                                    onChange={setDateFrom}
                                    placeholder="From"
                                    style={{ maxWidth: "140px" }}
                                />
                                <span className="mbt-date-sep">to</span>
                                <DateInputWithPicker
                                    value={dateTo}
                                    onChange={setDateTo}
                                    placeholder="To"
                                    style={{ maxWidth: "140px" }}
                                    fallbackMonth={dateFrom ? DateTime.fromISO(dateFrom) : undefined}
                                />
                            </div>

                            <div className="mbt-select-wrapper">
                                <select
                                    value={rail}
                                    onChange={e => setRail(e.target.value)}
                                    className={`mbt-select${rail !== "all" ? " is-active" : ""}`}
                                    aria-label="Filter by rail">
                                    <option value="all">All methods</option>
                                    {RAIL_OPTIONS.map(r => (
                                        <option key={r.value} value={r.value}>
                                            {r.label}
                                        </option>
                                    ))}
                                </select>
                                <Icon path={mdiChevronDown} size={0.75} className="mbt-select-icon" />
                            </div>

                            <div className="mbt-select-wrapper">
                                <select
                                    value={reconciled}
                                    onChange={e => setReconciled(e.target.value)}
                                    className={`mbt-select${reconciled !== "all" ? " is-active" : ""}`}
                                    aria-label="Filter by reconciled state">
                                    <option value="all">All</option>
                                    <option value="false">Unreconciled</option>
                                    <option value="true">Reconciled</option>
                                </select>
                                <Icon path={mdiChevronDown} size={0.75} className="mbt-select-icon" />
                            </div>

                            <div className="mbt-select-wrapper">
                                <select
                                    value={direction}
                                    onChange={e => setDirection(e.target.value)}
                                    className={`mbt-select${direction !== "all" ? " is-active" : ""}`}
                                    aria-label="Filter by money direction">
                                    {DIRECTION_OPTIONS.map(d => (
                                        <option key={d.value} value={d.value}>
                                            {d.label}
                                        </option>
                                    ))}
                                </select>
                                <Icon path={mdiChevronDown} size={0.75} className="mbt-select-icon" />
                            </div>

                            {hasActiveFilters && (
                                <button type="button" className="mbt-clear-filters-btn" onClick={handleClearFilters}>
                                    Clear filters
                                </button>
                            )}
                        </div>

                        {/* Net reconciled position — server-computed per currency (payments − refunds) */}
                        {summary.length > 0 && (
                            <div className="mbt-net-summary">
                                {summary.map(s => (
                                    <div className="mbt-net" key={s.currency}>
                                        <span className="mbt-net-label">Net external ({s.currency})</span>
                                        <span className="mbt-net-value">{formatAmount(s.net, s.currency)}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Reconcile ledger */}
                        {isSearching && results.length === 0 ? (
                            <p className="mbt-empty">Loading...</p>
                        ) : emptyMessage ? (
                            <p className="mbt-empty">{emptyMessage}</p>
                        ) : (
                            <div className="mbt-results">
                                {results.map(row => (
                                    <ReconcileRow key={row.id} row={row} onReconciled={handleReconciled} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReconcilePayments;
